<template>
	<div  id="customer-container">
		<div v-if="!serverComplete" id="loading">
			<span class="loading-icon"></span>
			<span class="loading-message">{{ busyText }}</span>
		</div>
		<h2>
			<span v-if="selectedCasino?.name">{{ selectedCasino.name }}</span> Customer WebPay Report
		</h2>
		<div id="customer-report-filters">
			<FilterDateTime :dateDefaults="dateDefaults" :showExportButton="showExportButton" />
		</div>
		<div class="casino-selector">
			<button id="refresh" title="Refresh play list" class="btn" @click="getCustomerWebPaySummary(true)">Refresh</button>
            <label for="limitOptions">Limit List</label>
			<select title="Set Limit" v-model="limit" id="limitOptions">
				<option v-for="(item, index) in limitOptions" :key="index" :value="item.value">{{ item.text }}</option>
			</select>
			<label for="casinoOptions" v-if="casinoListForReports.length > 1">Casino</label>
			<select title="Select Casino" id="casinoOptions" v-model="selectedCasinoId" v-if="casinoListForReports.length > 1">
				<option v-for="(casino, index) in casinoListForReports" :key="index" :value="casino.id">{{ casino.name }}</option>
			</select>
			<div>
				<button :disabled="serverBusy" class="prev-button btn" type="button" title="Previous" @click="previousPage()">Previous</button>
				<button :disabled="serverBusy" class="next-button btn" type="button" title="Next" @click="nextPage()">Next</button>
			</div>
			<span :currentPage="currentPage">page {{ currentPage }}</span>
			<button @click="exportExcel()" title="Export Report" class="apply-filters btn">Export Report</button>
		</div>
		<CustomerWebPayTable :customerWebPayList="customerWebPayList" :inGameCurrencyTool="inGameCurrencyTool" :systemCurrencyTool="systemCurrencyTool" :isMobile="isMobile" />
	</div>
</template>

<script>
import { onBeforeUnmount } from "vue";
import sharedScripts from "@/dependencies/sharedScripts";
import xlsxPerformStyles from "@/dependencies/xlsxPerformStyles";
import FilterDateTime from "@/components/FilterDateTime";
import CustomerWebPayTable from "@/components/CustomerWebPayTable.vue";

export default {
	name: "CustomerWebPayReport",
	components: {
		CustomerWebPayTable,
		FilterDateTime,
	},
	props: {
		reporterState: Object,
		casinoList: Array,
		isMobile: Boolean,
		serverVersion: Object,
		inGameCurrencyTool: Object,
		systemCurrencyTool: Object,
	},
	data() {
		return {
			status: Object.assign({}, this.globalStatus),
			selectedCasino: {},
			selectedCasinoId: this.reporterState.casinoId || this.selectedCasino.id,
			serverComplete: true,
			dateTime: new Date(),
			timeZoneOffset: new Date().getTimezoneOffset() / 60,
			dateDefaults: {},
			excelStartTime: 0,
			excelEndTime: 0,
			customerWebPayList: [],
			casinoListForReports: [],
			selectedCasino: {},
			cashierUserId: null,
			shiftNumber: null,
			startDate: new Date(new Date().setHours(0,0,0,0)),
			endDate: new Date(),
			offset: null,
			selectedCasinoId: this.reporterState.casinoId,
			dateFmt: "[$-409]m/d/yy\ h:mm\ AM/PM;@",
			currFmt: '"$"#,##0.00_);[Red]\("$"#,##0.00\)',
			systemSymbol: this.systemCurrencyTool.currencyInfo.symbol,
			systemMinorWholeOrFull: this.systemCurrencyTool.displayType.minorWholeOrFull,
			systemFull: this.systemCurrencyTool.displayType.full,
			showExportButton: true,
            limit: 20,
			currentPage:1,
		};
	},
	watch: {
		selectedCasinoId() {
			if (this.selectedCasinoId) {
				this.setSelectedCasino();
				this.getCustomerWebPaySummary();
			}
		},
	},
	created() {
		this.listCasinosForThisReporter();
		this.setExcellCurrencyFormat();

		let start = new Date(this.startDate);
		start = start.setHours(start.getHours() - this.timeZoneOffset);
		start = new Date(start).toISOString();
		let end = new Date();
		end = end.setHours(end.getHours() - this.timeZoneOffset);
		end = new Date(end).toISOString();
		this.dateDefaults = {
			startDate: start.slice(0, 16),
			endDate: end.slice(0, 16),
		};
		if (this.selectedCasinoId) {
			this.setSelectedCasino();
			this.getCustomerWebPaySummary();
		}
		this.eventBus.on("applyFilters", (payload) => {
			let error = false;
			if (!payload.startDate) {
				this.throwDateTimeError("Start Date");
				error = true;
			}
			if (!payload.endDate) {
				this.throwDateTimeError("End Date");
				error = true;
			}
			if (error) return;
			this.startDate = payload.startDate ? payload.startDate.slice(0, 16) : "";
			this.endDate = payload.endDate ? payload.endDate.slice(0, 16) : "";
			this.getCustomerWebPaySummary();
		});
		let thisInstance = this;
		onBeforeUnmount(() => {
			thisInstance.eventBus.off("applyFilters");
		});
	},
	methods: {
		setExcellCurrencyFormat() {
			let zeros = [];
			for (let index = 0; index < this.systemCurrencyTool.currencyInfo.minorDigits; index++) {
				zeros.push(0);
			}
			let decimalPlaces = this.systemCurrencyTool.currencyInfo.minorDigits > 0 ? `.${zeros.join("")}` : "";
			this.currFmt = `"${this.systemSymbol}"#,##0${decimalPlaces}_);[Red]\("${this.systemSymbol}"#,##0${decimalPlaces}\)`;
		},

		throwDateTimeError(input) {
			this.status.message = `${input} is not complete. ${input} is required. Must have both the date and time.`;
			this.status.ok = false;
			this.eventBus.emit("updateStatus", this.status);
		},
		listCasinosForThisReporter() {
			if (!this.reporterState?.isSysadmin) {
				let permissionsList = this.reporterState?.userPermissions?.sitePermissions;
				for (const key in permissionsList) {
					if (permissionsList[key].includes("SiteAdmin") || permissionsList[key].includes("Reporter")) {
						let casino = this.casinoList.filter((casino) => casino.id == key)[0];
						this.casinoListForReports.push(casino);
					}
				}
			} else {
				this.casinoListForReports = this.casinoList;
			}
		},
		setSelectedCasino() {
			this.selectedCasino = this.casinoListForReports.filter((casino) => casino.id == this.selectedCasinoId)[0];
		},
		previousPage() {
			if (this.currentPage == 1) return;
			this.currentPage--;
			this.offset = this.offset - this.limit;
			this.getCustomerWebPaySummary();
		},
		nextPage() {
			if (this.customerWebPayList?.rows.length < this.limit) return;
			this.offset = this.offset + this.limit;
			this.currentPage++;
			this.getCustomerWebPaySummary();
		},
		async getCustomerWebPaySummary(refresh = false) {
			this.busyText = `Loading Customer WebPay Report for ${this.selectedCasino.name}`;
			this.serverComplete = false;

			if(refresh){
				this.offset = 0;
				this.currentPage = 1;
			}

			// Check if session needs to be refreshed
			let success = await this.authenticationCheck(this);
			if (success.hasOwnProperty("ok") && !success.ok) {
				this.serverBusy = false;
				this.busyText = "";
				return false;
			}
			
			//This is here to count for the timezone difference and gets the times accurately
			let end = new Date(this.endDate);
			let start = new Date(this.startDate);

            let body = {
                StartDate: start,
                EndDate: end, 
                CasinoId: this.selectedCasino.id,
                Limit: this.limit,
				Offset: this.offset
            };

			let headerObj = new Headers();
			headerObj.append("Authorization", `Bearer ${this.reporterState.accessToken}`);
			headerObj.append("Content-Type", "application/json; charset=utf-8");
			let requestUrl = new URL("/api/v1/report/support/webpay", this.rabbitsfootHostUrl);
			let params = requestUrl.searchParams;

			requestUrl.search = params.toString();

			let request = new Request(requestUrl.toString(), {
				method: "POST",
                body: JSON.stringify(body),
				headers: headerObj,
			});

			try {
				let response = await fetch(request);

				let fetchStatus = sharedScripts.checkFetchErrors(response);

				if (fetchStatus && !fetchStatus.ok) {
					this.serverComplete = true;
					this.eventBus.emit("updateStatus", fetchStatus);
					if (fetchStatus.code === 409) this.eventBus.emit("forceLogout");
					return false;
				}

				let dataJson = await response.json();

				this.customerWebPayList = dataJson;
				this.dateTime = new Date();

				this.serverComplete = true;
				this.busyText = "";
			} catch (e) {
				this.serverComplete = true;
				this.busyText = "";
				this.status.ok = false;
				this.status.message = e;
				console.error(e);
			}
		},
		async exportExcel() {
			if (this.customerWebPayList.length === 0) {
				this.status.message = "Nothing to export";
				this.status.ok = false;
				this.eventBus.emit("updateStatus", this.status);
				return false;
			}

			this.fileNameDateRange = this.dateTime.toLocaleTimeString([], this.shortDateOptions).replace(", ", "_");
			this.excelStartTime = this.isoDateToExcelDate(this.customerWebPayList.startDate,true, true);
			this.excelEndTime = this.isoDateToExcelDate(this.customerWebPayList.endDate,true, true);

			const workbook = new this.ExcelJS.Workbook();
			workbook.creator = "Reporter App";
			workbook.lastModifiedBy = "Reporter App";
			workbook.created = new Date();
			workbook.modified = new Date();

			let contextRows = [
				[this.selectedCasino.name],
				["Customer WebPay Report"],
				[this.serverVersion.serverName],
				["From:", this.excelStartTime],
				["To:", this.excelEndTime],
			];

			// header row
			const worksheet = workbook.addWorksheet("Customer WebPay Report");

			worksheet.columns = [
				{ header: "User ID", key: "userId", width: 55 },
                { header: "Transaction ID", key: "transactionId", width: 55 },
                { header: "Provider Transaction ID", key: "providerTransactionId", width: 30 },
				{ header: "Transaction Date Time", key: "transactionDateTime", width: 30 },
				{ header: "Phone Number", key: "userPhoneNumber", width: 25 },
				{ header: "Provider", key: "provider", width: 15 },
				{ header: "Amount", key: "amount", width: 25 },
				{ header: "Result", key: "result", width: 25 },
				{ header: "Extra Information", key: "extraInformation", width: 100 },
			];

			worksheet.insertRows(1, contextRows);
			worksheet.getCell("B4").numFmt = this.dateFmt;
			worksheet.getCell("B5").numFmt = this.dateFmt;

			worksheet.eachRow(function (row, rowNumber) {
				row.font = xlsxPerformStyles.defaultStyles.font;
			});

			worksheet.getColumn("transactionDateTime").numFmt = this.dateFmt;


			let rowOffset = worksheet.rowCount;

			this.setStylesForCells(worksheet, xlsxPerformStyles.headerStyles, rowOffset);

			this.customerWebPayList.rows.forEach((customer, index) => {
				let data = {};

				data.userId = customer.userId;
				data.transactionId = customer.monetaryTransactionId;
				data.providerTransactionId = customer.providerTransactionId;
				data.transactionDateTime = this.isoDateToExcelDate(customer.initiationDate,false, true);
				data.userPhoneNumber = customer.phoneNumber;
                data.provider = customer.provider;
                data.amount = customer.fromPlayerCashCOC;
                data.result = customer.status;
                data.extraInformation = customer.statusAdditionalInfo;


				worksheet.addRow(data);
				rowOffset = worksheet.rowCount;

				// Rows are not zero indexed and also need to skip header row so, index + rowOffset
				this.setStylesForCells(worksheet, xlsxPerformStyles.basicStyles, rowOffset);

				// Fill every even numbered row after header row.
				// Rows are not zero indexed so we actually are selecting for odd since we want the even rows AFTER header
				if (index % 2 !== 0) this.setSingleStyleForCells(worksheet, "fill", xlsxPerformStyles.oddFill, rowOffset);
			});

			worksheet.addRow();
			rowOffset = worksheet.rowCount;
			this.setStylesForCells(worksheet, xlsxPerformStyles.headerStyles, rowOffset);

			worksheet.getColumn(1).alignment = "left";
			worksheet.getColumn(2).alignment = "left";
			worksheet.getColumn(3).alignment = "left";

			workbook.xlsx
				.writeBuffer()
				.then((data) => {
					const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8" });
					saveAs(blob, `Customer_WebPay_Report_${this.fileNameDateRange}.xlsx`);
				})
				.catch((e) => {
					this.status.message = `Failed to exprort spreadsheet: ${e}`;
					this.status.ok = false;
					this.eventBus.emit("updateStatus", this.status);
					this.serverComplete = true;
					console.error(e);
					return false;
				});
		},
	},
};
</script>

<style>
#customer-report-filters .export {
	display: none;
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#loading {
	display: grid;
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	padding: 30px;
	background-color: rgb(0 0 0 / 70%);
	z-index: 9999;
	transition: background-color 0.3s ease-in-out;
	text-align: center;
	align-content: center;
}

.casino-selector {
	display: flex;
	justify-content: center;
	align-items: center;
}

#filter-container {
	display: flex;
	justify-content: center;
}

@media (max-width: 767px) {
	.casino-selector {
		display: grid;
	}
}
</style>
